// client/src/components/modals/messaging/ConversationModal.jsx
//
// Unified messaging modal — one component, three roles:
//
//  role="student"
//    Layout: single-pane chat
//    Props:  messages, recipient (instructorInfo obj), currentUserId,
//            messageText, onMessageTextChange, onSendMessage, isLoading
//
//  role="instructor"
//    Layout: left sidebar (student list) + right pane (chat or announcement compose)
//    Props:  messages, students, selectedStudent, onSelectStudent,
//            currentUserId, messageText, onMessageTextChange, onSendMessage,
//            studentsWithUnread,
//            — announcement tab —
//            announcementTitle, onAnnouncementTitleChange,
//            announcementBody,  onAnnouncementBodyChange,
//            announcementPriority, onAnnouncementPriorityChange,
//            announcementHistory, onSendAnnouncement, sendingAnnouncement,
//            onViewAnnouncement  (fn(announcement) → opens detail modal)
//
//  role="admin"  (used by AdminMessaging page, broadcast mode)
//    Layout: left sidebar (recipient selector + stats) + right pane (compose + history)
//    Props:  students, instructors,
//            recipientType, onRecipientTypeChange,
//            messageData { title, body }, onMessageDataChange,
//            announcementPriority, onAnnouncementPriorityChange,
//            announcementHistory, onSendBroadcast, sendingAnnouncement,
//            isLoading
//
// NOTE: This component is NOT a modal overlay — it renders inline inside
//       whatever container the parent provides (tabs, page area, etc.).
//       The AnnouncementDetailModal is used separately for viewing details.

import React, { useState } from 'react';
import './ConversationModal.css';

/* ─────────────────────────────────────────────
   Shared sub-components
───────────────────────────────────────────── */

/** Single message bubble */
const MessageBubble = ({ msg, isSentByMe, senderLabel }) => (
  <div className={`cm-msg-wrapper ${isSentByMe ? 'cm-sent' : 'cm-received'}`}>
    <div className={`cm-bubble ${isSentByMe ? 'cm-bubble-mine' : 'cm-bubble-theirs'}`}>
      <div className="cm-bubble-header">
        <span className="cm-bubble-sender">{isSentByMe ? 'You' : senderLabel}</span>
        <span className="cm-bubble-time">
          {msg.createdAt
            ? new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : ''}
        </span>
      </div>
      <div className="cm-bubble-body">{msg.body}</div>
      {isSentByMe && (
        <div className="cm-bubble-status">
          <span className={`cm-status-dot ${msg.deliveryStatus || 'sent'}`}>
            {msg.deliveryStatus === 'seen'      ? '✓✓' :
             msg.deliveryStatus === 'delivered' ? '✓'  : '○'}
          </span>
        </div>
      )}
    </div>
  </div>
);

/** Conversation pane — messages list + send form */
const ChatPane = ({
  messages = [],
  isLoading,
  recipient,          // { name, imageUrl?, email? }
  currentUserId,
  messageText,
  onMessageTextChange,
  onSendMessage,
  emptyMessage = 'No messages yet. Start the conversation!',
  placeholder = 'Type your message…',
}) => (
  <div className="cm-chat-pane">
    {/* Recipient header */}
    {recipient && (
      <div className="cm-chat-header">
        <img
          src={recipient.imageUrl || '/images/default-avatar.png'}
          alt={recipient.name}
          className="cm-chat-avatar"
          onError={e => { e.target.src = '/images/default-avatar.png'; }}
        />
        <div className="cm-chat-user-info">
          <span className="cm-chat-name">{recipient.name}</span>
          {recipient.email && (
            <span className="cm-chat-sub">{recipient.email}</span>
          )}
        </div>
      </div>
    )}

    {/* Messages */}
    <div className="cm-messages-scroll">
      {isLoading ? (
        <p className="cm-empty">Loading messages…</p>
      ) : messages.length > 0 ? (
        messages.map((msg, idx) => {
          const isMine = msg.from === currentUserId || msg.senderUserId === currentUserId;
          return (
            <MessageBubble
              key={msg._id || idx}
              msg={msg}
              isSentByMe={isMine}
              senderLabel={recipient?.name || 'Other'}
            />
          );
        })
      ) : (
        <p className="cm-empty">{emptyMessage}</p>
      )}
    </div>

    {/* Send form */}
    <form onSubmit={onSendMessage} className="cm-send-form">
      <input
        type="text"
        className="cm-send-input"
        value={messageText}
        onChange={e => onMessageTextChange(e.target.value)}
        placeholder={placeholder}
      />
      <button type="submit" className="cm-send-btn">📤 Send</button>
    </form>
  </div>
);

/** Announcement history card (read-only row) */
const AnnouncementHistoryCard = ({ announcement, onView }) => {
  const priorityConfig = {
    urgent: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
    high:   { color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
    medium: { color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
    low:    { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  };
  const key = announcement.priority?.toLowerCase() || 'medium';
  const p = priorityConfig[key] ?? priorityConfig.medium;

  return (
    <div
      className="cm-ann-card"
      style={{ '--cm-card-accent': p.color, cursor: onView ? 'pointer' : 'default' }}
      onClick={() => onView?.(announcement)}
    >
      <div className="cm-ann-card-inner">
        <div className="cm-ann-card-header">
          <h4 className="cm-ann-card-title">{announcement.title}</h4>
          <span
            className="cm-ann-priority-badge"
            style={{ background: p.bg, color: p.color, border: `1px solid ${p.border}` }}
          >
            {key.toUpperCase()}
          </span>
        </div>
        <p className="cm-ann-card-body">{announcement.body}</p>
        <div className="cm-ann-card-footer">
          <span className="cm-ann-date">
            📅 {new Date(announcement.createdAt).toLocaleString()}
          </span>
          {announcement.recipients?.length != null && (
            <span className="cm-ann-recipients">
              📨 {announcement.recipients.length} recipients
            </span>
          )}
          {announcement.sentCount != null && (
            <span className="cm-ann-recipients">📨 {announcement.sentCount} sent</span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Role layouts
───────────────────────────────────────────── */

/** STUDENT — single-pane chat with instructor */
const StudentLayout = ({
  messages, isLoading, recipient, currentUserId,
  messageText, onMessageTextChange, onSendMessage,
  studentAnnouncements, onViewAnnouncement,
}) => {
  const [tab, setTab] = useState('messages');

  return (
    <div className="cm-root cm-split">
      <div className="cm-right" style={{ width: '100%' }}>

        {/* Internal tabs */}
        <div className="cm-right-tabs">
          <button
            className={`cm-right-tab ${tab === 'messages' ? 'cm-right-tab-active' : ''}`}
            onClick={() => setTab('messages')}
          >
            💬 Messages
          </button>
          <button
            className={`cm-right-tab ${tab === 'announcements' ? 'cm-right-tab-active' : ''}`}
            onClick={() => setTab('announcements')}
          >
            📢 Announcements
          </button>
        </div>

        {/* Messages tab */}
        {tab === 'messages' && (
          <ChatPane
            messages={messages}
            isLoading={isLoading}
            recipient={recipient}
            currentUserId={currentUserId}
            messageText={messageText}
            onMessageTextChange={onMessageTextChange}
            onSendMessage={onSendMessage}
            emptyMessage="No messages yet. Start a conversation with your instructor!"
            placeholder="Type your message…"
          />
        )}

        {/* Announcements tab */}
        {tab === 'announcements' && (
          <div className="cm-ann-pane">
            <div className="cm-ann-history">
              <h4 className="cm-ann-history-title">📢 Course Announcements</h4>
              {studentAnnouncements?.length > 0 ? (
                studentAnnouncements.map((ann, idx) => (
                  <AnnouncementHistoryCard
                    key={idx}
                    announcement={ann}
                    onView={onViewAnnouncement}
                  />
                ))
              ) : (
                <p className="cm-ann-empty">📭 No announcements yet</p>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/** INSTRUCTOR — sidebar student list + chat or announcement compose */
const InstructorLayout = ({
  // messaging
  messages, students, selectedStudent, onSelectStudent, studentsWithUnread,
  currentUserId, messageText, onMessageTextChange, onSendMessage,
  // announcements
  announcementTitle, onAnnouncementTitleChange,
  announcementBody,  onAnnouncementBodyChange,
  announcementPriority, onAnnouncementPriorityChange,
  announcementHistory, onSendAnnouncement, sendingAnnouncement,
  onViewAnnouncement,
}) => {
  const [rightTab, setRightTab] = useState('messages'); // 'messages' | 'announcements'
  const [showComposeModal, setShowComposeModal] = useState(false);

  return (
    <div className="cm-root cm-split">

      {/* ── Left: student list ── */}
      <div className="cm-sidebar">
        <div className="cm-sidebar-header">
          <h4>👥 Students ({students.length})</h4>
        </div>
        <div className="cm-sidebar-list">
          {students.length === 0 && (
            <p className="cm-sidebar-empty">No enrolled students</p>
          )}
          {students.map(student => {
            const loginId = student.loginId || student.studentId;
            const unread  = studentsWithUnread?.includes(loginId);
            const active  = selectedStudent?.loginId === loginId || selectedStudent?.studentId === loginId;
            return (
              <div
                key={loginId}
                className={`cm-student-row ${active ? 'cm-student-active' : ''}`}
                onClick={() => onSelectStudent(student)}
              >
                <img
                  src={student.imageUrl || '/images/default-avatar.png'}
                  alt={student.name}
                  className="cm-student-avatar"
                  onError={e => { e.target.src = '/images/default-avatar.png'; }}
                />
                <div className="cm-student-info">
                  <span className="cm-student-name">{student.name}</span>
                  <span className="cm-student-sub">{student.email || ''}</span>
                </div>
                {unread && <span className="cm-unread-dot" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Right pane ── */}
      <div className="cm-right">

        {/* Tab bar */}
        <div className="cm-right-tabs">
          <button
            className={`cm-right-tab ${rightTab === 'messages' ? 'cm-right-tab-active' : ''}`}
            onClick={() => setRightTab('messages')}
          >
            💬 Messages
          </button>
          <button
            className={`cm-right-tab ${rightTab === 'announcements' ? 'cm-right-tab-active' : ''}`}
            onClick={() => setRightTab('announcements')}
          >
            📢 Announcements
          </button>
        </div>

        {/* Messages tab */}
        {rightTab === 'messages' && (
          selectedStudent ? (
            <ChatPane
              messages={messages}
              recipient={{
                name: selectedStudent.name,
                imageUrl: selectedStudent.imageUrl,
                email: selectedStudent.email,
              }}
              currentUserId={currentUserId}
              messageText={messageText}
              onMessageTextChange={onMessageTextChange}
              onSendMessage={onSendMessage}
              emptyMessage="No messages yet. Send a message to this student."
              placeholder={`Message ${selectedStudent.name}…`}
            />
          ) : (
            <div className="cm-no-selection">
              <span className="cm-no-selection-icon">💬</span>
              <p>Select a student to view conversation</p>
            </div>
          )
        )}

        {/* Announcements tab */}
        {rightTab === 'announcements' && (
          <div className="cm-ann-pane">

            {/* History header with + button */}
            <div className="cm-ann-history">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <h4 className="cm-ann-history-title">📋 Sent Announcements</h4>
                <button
                  className="cm-ann-add-btn"
                  onClick={() => setShowComposeModal(true)}
                  title="New Announcement"
                >
                  + New
                </button>
              </div>
              {announcementHistory?.length > 0 ? (
                announcementHistory.map((ann, idx) => (
                  <AnnouncementHistoryCard
                    key={idx}
                    announcement={ann}
                    onView={onViewAnnouncement}
                  />
                ))
              ) : (
                <p className="cm-ann-empty">No announcements sent yet</p>
              )}
            </div>

            {/* Compose Modal */}
            {showComposeModal && (
              <div className="cm-compose-overlay" onClick={() => setShowComposeModal(false)}>
                <div className="cm-compose-shell" onClick={e => e.stopPropagation()}>

                  {/* Header */}
                  <div className="cm-compose-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="cm-compose-icon">📢</div>
                      <div>
                        <div className="cm-compose-title">New Announcement</div>
                        <div className="cm-compose-sub">Send to all enrolled students</div>
                      </div>
                    </div>
                    <button className="cm-compose-close" onClick={() => setShowComposeModal(false)}>✕</button>
                  </div>

                  {/* Body */}
                  <div className="cm-compose-body">
                    <div className="cm-ann-field">
                      <label className="cm-ann-label">Priority</label>
                      <select
                        className="cm-ann-select"
                        value={announcementPriority}
                        onChange={e => onAnnouncementPriorityChange(e.target.value)}
                      >
                        <option value="low">🟢 Low</option>
                        <option value="medium">🟡 Medium</option>
                        <option value="high">🟠 High</option>
                        <option value="urgent">🔴 Urgent</option>
                      </select>
                    </div>

                    <div className="cm-ann-field">
                      <label className="cm-ann-label">Title *</label>
                      <input
                        type="text"
                        className="cm-ann-input"
                        value={announcementTitle}
                        onChange={e => onAnnouncementTitleChange(e.target.value)}
                        placeholder="Announcement title…"
                        maxLength={100}
                      />
                    </div>

                    <div className="cm-ann-field">
                      <label className="cm-ann-label">Message *</label>
                      <textarea
                        className="cm-ann-textarea"
                        value={announcementBody}
                        onChange={e => onAnnouncementBodyChange(e.target.value)}
                        placeholder="Write your announcement…"
                        rows={6}
                        maxLength={500}
                      />
                      <small className="cm-ann-char-count">{announcementBody?.length || 0}/500</small>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="cm-compose-footer">
                    <button className="cm-compose-cancel" onClick={() => setShowComposeModal(false)}>
                      Cancel
                    </button>
                    <button
                      className="cm-ann-send-btn"
                      onClick={() => { onSendAnnouncement(); setShowComposeModal(false); }}
                      disabled={sendingAnnouncement}
                    >
                      {sendingAnnouncement ? '📤 Sending…' : '📢 Send to All Students'}
                    </button>
                  </div>

                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

/** ADMIN — broadcast compose + history (AdminMessaging page) */
const AdminLayout = ({
  students, instructors,
  recipientType, onRecipientTypeChange,
  messageData, onMessageDataChange,
  announcementPriority, onAnnouncementPriorityChange,
  onSendBroadcast, sendingAnnouncement,
  isLoading,
}) => {
  const recipientCount =
    recipientType === 'students'    ? students.length    :
    recipientType === 'instructors' ? instructors.length :
    students.length + instructors.length;

  if (isLoading) {
    return (
      <div className="cm-root cm-single cm-loading">
        <div className="cm-spinner" />
        <p>Loading users…</p>
      </div>
    );
  }

  return (
    <div className="cm-root cm-split">

      {/* ── Left: recipient selector + stats ── */}
      <div className="cm-sidebar">
        <div className="cm-sidebar-header">
          <h4>📋 Recipients</h4>
        </div>
        <div className="cm-admin-sidebar-body">
          <div className="cm-ann-field">
            <label className="cm-ann-label">Send to</label>
            <select
              className="cm-ann-select"
              value={recipientType}
              onChange={e => onRecipientTypeChange(e.target.value)}
            >
              <option value="students">📚 Students ({students.length})</option>
              <option value="instructors">👨‍🏫 Instructors ({instructors.length})</option>
              <option value="all">👥 All ({students.length + instructors.length})</option>
            </select>
          </div>

          {/* Warning */}
          <div className="cm-broadcast-warning">
            <span>⚠️</span>
            <p>
              This sends to{' '}
              {recipientType === 'students'    ? 'all students'    :
               recipientType === 'instructors' ? 'all instructors' :
               'ALL users'}
            </p>
          </div>

          {/* Stat cards */}
          <div className="cm-stat-cards">
            {recipientType !== 'instructors' && (
              <div className="cm-stat-card">
                <span className="cm-stat-label">📚 Students</span>
                <span className="cm-stat-value">{students.length}</span>
              </div>
            )}
            {recipientType !== 'students' && (
              <div className="cm-stat-card">
                <span className="cm-stat-label">👨‍🏫 Instructors</span>
                <span className="cm-stat-value">{instructors.length}</span>
              </div>
            )}
            <div className="cm-stat-card cm-stat-card-total">
              <span className="cm-stat-label">👥 Total</span>
              <span className="cm-stat-value">{recipientCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: compose + history ── */}
      <div className="cm-right">
        <div className="cm-ann-pane">

          {/* Compose */}
          <div className="cm-ann-compose">
            <h4 className="cm-ann-compose-title">📢 Compose Announcement</h4>

            <div className="cm-ann-field">
              <label className="cm-ann-label">Priority</label>
              <select
                className="cm-ann-select"
                value={announcementPriority}
                onChange={e => onAnnouncementPriorityChange(e.target.value)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className="cm-ann-field">
              <label className="cm-ann-label">Title *</label>
              <input
                type="text"
                className="cm-ann-input"
                value={messageData.title}
                onChange={e => onMessageDataChange({ ...messageData, title: e.target.value })}
                placeholder="e.g., Important: Platform Maintenance Notice"
                maxLength={100}
              />
            </div>

            <div className="cm-ann-field">
              <label className="cm-ann-label">Message *</label>
              <textarea
                className="cm-ann-textarea"
                value={messageData.body}
                onChange={e => onMessageDataChange({ ...messageData, body: e.target.value })}
                placeholder="Write your announcement message here…"
                rows={6}
                maxLength={500}
              />
              <small className="cm-ann-char-count">{messageData.body?.length || 0}/500</small>
            </div>

            <button
              className="cm-ann-send-btn cm-ann-send-btn-admin"
              onClick={onSendBroadcast}
              disabled={sendingAnnouncement}
            >
              {sendingAnnouncement ? '📤 Sending…' : '📢 Send Announcement'}
            </button>
          </div>

          {/* History */}
          {/* <div className="cm-ann-history">
            <h4 className="cm-ann-history-title">📋 Announcement History</h4>
            {announcementHistory?.length > 0 ? (
              announcementHistory.map((ann, idx) => (
                <AnnouncementHistoryCard key={idx} announcement={ann} />
              ))
            ) : (
              <p className="cm-ann-empty">No announcements sent yet</p>
            )}
          </div> */}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main export
───────────────────────────────────────────── */
const ConversationModal = ({ role = 'student', ...props }) => {
  if (role === 'student')     return <StudentLayout    {...props} />;
  if (role === 'instructor')  return <InstructorLayout {...props} />;
  if (role === 'admin')       return <AdminLayout      {...props} />;
  return null;
};

export default ConversationModal;
